import { useFeatureGate } from '@trello/feature-gate-client';

import { useAtlassianIntelligenceInProgressFragment } from './AtlassianIntelligenceInProgressFragment.generated';

const LOADING_TIMEOUT_MS = 60 * 1000;

/**
 * Determines if a card is currently being generated/updated by AI
 *
 * @param cardId - The card ID to check
 * @returns boolean indicating if the card is currently loading from AI
 */
export const useAtlassianIntelligenceInProgress = (cardId: string): boolean => {
  const { value: isCardLoadingEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const { data: card } = useAtlassianIntelligenceInProgressFragment({
    from: { id: cardId },
    returnPartialData: true,
    optimistic: true,
  });

  if (!isCardLoadingEnabled || !card) {
    return false;
  }

  const {
    creationMethodError,
    creationMethod,
    badges,
    creationMethodLoadingStartedAt,
  } = card;

  if (
    creationMethodError ||
    creationMethod !== 'ai' ||
    !badges?.lastUpdatedByAi ||
    !creationMethodLoadingStartedAt
  ) {
    return false;
  }

  const loadingStartedAt = new Date(creationMethodLoadingStartedAt).getTime();
  const sixtySecondsAgo = new Date().getTime() - LOADING_TIMEOUT_MS;

  return loadingStartedAt >= sixtySecondsAgo;
};
